/**
 * Single source of truth for "how does a child's avatar look?".
 *
 * Renders the uploaded photo when `avatarUrl` is set; otherwise falls
 * back to the colored circle with the child's initials we used before
 * profile photos existed. Used on the home page, children page, map
 * sidebar, pickup card, panic dialog, emergency mode — everywhere a
 * child is depicted.
 *
 * Why a component: keeps the fallback rule in one place so we don't
 * end up with one surface showing "VI" and another showing the photo
 * because they had different conditionals.
 */
import { cn } from '@/lib/utils';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

interface ChildAvatarProps {
  name: string;
  color?: string | null;
  /** Relative path from the users service, e.g. `/v1/children/.../avatar?v=…`.
   *  Falsy → render initials. */
  avatarUrl?: string | null;
  /** Tailwind `size-*` shorthand. Default `size-12` (48 px) reads well
   *  on a desktop sidebar; smaller surfaces can opt down with the prop. */
  size?: string;
  /** Extra classes for the wrapper — useful for border / ring / shadow. */
  className?: string;
}

export function ChildAvatar({
  name,
  color,
  avatarUrl,
  size = 'size-12',
  className,
}: ChildAvatarProps) {
  if (avatarUrl) {
    return (
      <img
        src={`${API_BASE}${avatarUrl}`}
        alt=""
        className={cn(size, 'shrink-0 rounded-full object-cover', className)}
      />
    );
  }
  return (
    <span
      className={cn(
        size,
        'grid shrink-0 place-items-center rounded-full text-xs font-semibold text-white',
        className,
      )}
      style={{ backgroundColor: color ?? '#4F6BFF' }}
    >
      {name.slice(0, 2).toUpperCase()}
    </span>
  );
}
